import styled from '@emotion/styled';
import { dangerousGettext, gettext } from '@eventbrite/i18n';
import { LoadingButton, Typography } from '@eventbrite/marmalade';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAppContext } from '../../../AppContext';
import {
    Alert,
    AlertProps,
    defaultAlertErrorMessage,
    FormSection,
    SIGN_IN_PATH,
} from '../../../shared';
import { lookupUserByEmail } from '../api';
import { CreateAccountFormValues } from '../types';
import { isValidEmail } from '../utils';
import { ConfirmEmailFormField } from './ConfirmEmailFormField';
import { CreateAccountActionBar } from './CreateAccountActionBar';
import { CreateAccountTermsCheckbox } from './CreateAccountTermsCheckbox';
import { EmailFormField } from './EmailFormField';
import { NameFormField } from './NameFormField';
import { PasswordFormField } from './PasswordFormField';

export enum CreateAccountFormFieldsTestId {
    Container = 'CreateAccountFormFieldsContainer',
    EmailContinueButton = 'CreateAccountFormEmailContinueButton',
}

export const FormFields = ({
    alertMessage,
    setAlertMessage,
    isMobile,
    hasSubmissionError,
    isSubmitting,
}: {
    alertMessage: AlertProps | null;
    setAlertMessage: Function;
    isMobile: boolean;
    hasSubmissionError?: boolean;
    isSubmitting: boolean;
}) => {
    const { watch } = useFormContext<CreateAccountFormValues>();
    const enteredEmail = watch('email');
    const [isLoading, setIsLoading] = useState(false);

    const {
        didCreateAccount,
        eventCreationFailed,
        shouldShowFullSignUpForm,
        enteredEmailExists,
        isSubmitFormButtonDisabled,
        updateContext,
    } = useAppContext();

    useEffect(() => {
        if (hasSubmissionError) {
            setAlertMessage(defaultAlertErrorMessage);
        }
    }, [hasSubmissionError, setAlertMessage]);

    useEffect(() => {
        const lookupUserByEmailAddress = async (email: string) => {
            setIsLoading(true);
            const { exists } = (await lookupUserByEmail(email)) || {};

            if (exists) {
                updateContext({
                    enteredEmailExists: true,
                    isSubmitFormButtonDisabled: true,
                });
                setAlertMessage({
                    title: gettext('Email already exists').toString(),
                    description: (
                        // @ts-expect-error Typography isn't used to dangerouslySetInnerHTML.
                        <Typography
                            as="p"
                            color="neutral-800"
                            variant="body-md"
                            className="checkbox-label-text"
                            dangerouslySetInnerHTML={{
                                __html: dangerousGettext(
                                    'This email is already associated with an account. Please <a href="%(signInPath)s" target="_self">log in</a> or use a different email address.',
                                    {
                                        signInPath: SIGN_IN_PATH,
                                    },
                                ).toString(),
                            }}
                        />
                    ),
                    variant: 'warning',
                });
            } else if (hasSubmissionError) {
                // initial error submitting the form still present, set the alert to the default error message
                setAlertMessage(defaultAlertErrorMessage);
                updateContext({
                    enteredEmailExists: false,
                    isSubmitFormButtonDisabled: false,
                });
            } else {
                // no error, clear the alert
                setAlertMessage(null);
                updateContext({
                    enteredEmailExists: false,
                    isSubmitFormButtonDisabled: false,
                });
            }
            setIsLoading(false);
        };

        if (isValidEmail(enteredEmail) && !didCreateAccount) {
            lookupUserByEmailAddress(enteredEmail);
        }
    }, [
        didCreateAccount,
        enteredEmail,
        hasSubmissionError,
        setAlertMessage,
        enteredEmailExists,
        updateContext,
    ]);

    const handleEmailContinue = () => {
        if (
            isValidEmail(enteredEmail) &&
            !!enteredEmail &&
            !enteredEmailExists &&
            !isLoading
        ) {
            updateContext({ shouldShowFullSignUpForm: true });
        }
    };

    return (
        <div data-testid={CreateAccountFormFieldsTestId.Container}>
            <FormSection spacing={16}>
                <EmailFormField />
            </FormSection>
            {!shouldShowFullSignUpForm && (
                <div>
                    <LoadingButton
                        isLoading={isLoading}
                        data-testid={
                            CreateAccountFormFieldsTestId.EmailContinueButton
                        }
                        variant="primary"
                        size="block"
                        onClick={() => {
                            handleEmailContinue();
                        }}
                    >
                        {gettext('Continue')}
                    </LoadingButton>
                    <LoginContainer>
                        {/* @ts-expect-error Typography isn't used to dangerouslySetInnerHTML. */}
                        <Typography
                            as="p"
                            color="neutral-800"
                            variant="body-md"
                            dangerouslySetInnerHTML={{
                                __html: dangerousGettext(
                                    'Have an existing account? <a href="%(signInPath)s">Log in instead</a>',
                                    {
                                        signInPath: SIGN_IN_PATH,
                                    },
                                ).toString(),
                            }}
                        />
                    </LoginContainer>
                </div>
            )}
            {shouldShowFullSignUpForm && (
                <>
                    <FormSection spacing={16}>
                        <ConfirmEmailFormField />
                    </FormSection>
                    {isMobile ? (
                        <>
                            <FormSection spacing={16}>
                                <NameFormField type="firstName" />
                            </FormSection>
                            <FormSection spacing={16}>
                                <NameFormField type="lastName" />
                            </FormSection>
                        </>
                    ) : (
                        <FormSection spacing={16}>
                            <NameFields>
                                <NameField>
                                    <NameFormField type="firstName" />
                                </NameField>
                                <NameField>
                                    <NameFormField type="lastName" />
                                </NameField>
                            </NameFields>
                        </FormSection>
                    )}
                    <FormSection spacing={24}>
                        <PasswordFormField />
                    </FormSection>

                    <FormSection spacing={16}>
                        <CreateAccountTermsCheckbox />
                    </FormSection>

                    {(!didCreateAccount || isSubmitting) &&
                        !eventCreationFailed &&
                        shouldShowFullSignUpForm && (
                            <CreateAccountActionBar
                                isSubmitting={isSubmitting}
                                isSubmitButtonDisabled={
                                    isSubmitFormButtonDisabled
                                }
                            />
                        )}
                </>
            )}

            {(hasSubmissionError || enteredEmailExists) && alertMessage && (
                <Alert
                    title={alertMessage.title}
                    description={alertMessage.description}
                    variant={alertMessage.variant}
                    icon={alertMessage.icon}
                />
            )}
        </div>
    );
};

const NameFields = styled.div`
    display: flex;
    gap: 16px;
    width: 100%;
`;

const NameField = styled.div`
    width: 50%;
`;

const LoginContainer = styled.div`
    padding: 24px 0;
`;
